/**
 * Audio: a procedural medieval score and a set of synthesised effects.
 *
 * There are no audio files, no samples and no audio library — every sound is
 * synthesised with the Web Audio API at runtime. The music is built from a small
 * amount of theory: a drone, a plucked lute figure and a slow melody in the
 * Dorian mode over a hand-drum pulse, cycling through a four-chord loop. It is
 * fully deterministic and repeats every four bars (~29 s); nothing here quotes
 * or reproduces any existing tune. Effects are built from short noise bursts and
 * detuned partials so a sword lands like metal, not like a beep.
 *
 * See docs/LICENSING.md — style and mode are not protectable, and with no
 * recording sampled there is nothing to infringe.
 */

type Kind =
  | 'click' | 'attack' | 'arrow' | 'build' | 'train' | 'death' | 'ping'
  | 'notice' | 'meteor' | 'coin' | 'capture' | 'error';

/** A Dorian-ish minor scale, the workhorse of "medieval" pastiche. */
const SCALE = [0, 2, 3, 5, 7, 9, 10];
const ROOT = 146.83;                     // D3
const CHORDS = [0, 0, 5, 5, 3, 3, 4, 4]; // i i iv iv III III v v

function noteFreq(step: number, octave = 0): number {
  const s = ((step % SCALE.length) + SCALE.length) % SCALE.length;
  const oct = octave + Math.floor(step / SCALE.length);
  return ROOT * Math.pow(2, oct + SCALE[s] / 12);
}

class Audio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private noise: AudioBuffer | null = null;

  musicVolume = Number(localStorage.getItem('kaelmoor.music') ?? 0.35);
  sfxVolume = Number(localStorage.getItem('kaelmoor.sfx') ?? 0.6);

  private musicOn = false;
  private beat = 0;
  private nextNoteAt = 0;
  private timer: number | null = null;
  private lastAt = new Map<Kind, number>();

  /** The browser only allows audio after a gesture, so this is called on click. */
  unlock() {
    this.ensure();
    if (this.ctx && this.ctx.state === 'suspended') this.ctx.resume();
  }

  private ensure() {
    if (this.ctx) return;
    try {
      this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      this.master = this.ctx.createGain();
      this.master.gain.value = 1;
      this.master.connect(this.ctx.destination);

      this.musicGain = this.ctx.createGain();
      this.musicGain.gain.value = this.musicVolume;
      this.musicGain.connect(this.master);

      this.sfxGain = this.ctx.createGain();
      this.sfxGain.gain.value = this.sfxVolume;
      this.sfxGain.connect(this.master);

      // one second of white noise, reused by every percussive effect
      const len = this.ctx.sampleRate;
      this.noise = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
      const data = this.noise.getChannelData(0);
      for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    } catch { /* audio is optional */ }
  }

  setMusicVolume(v: number) {
    this.musicVolume = Math.max(0, Math.min(1, v));
    localStorage.setItem('kaelmoor.music', String(this.musicVolume));
    if (this.musicGain) this.musicGain.gain.value = this.musicVolume;
  }

  setSfxVolume(v: number) {
    this.sfxVolume = Math.max(0, Math.min(1, v));
    localStorage.setItem('kaelmoor.sfx', String(this.sfxVolume));
    if (this.sfxGain) this.sfxGain.gain.value = this.sfxVolume;
  }

  /* ------------------------------------------------------------- music */

  startMusic() {
    this.ensure();
    if (!this.ctx || this.musicOn) return;
    this.musicOn = true;
    this.nextNoteAt = this.ctx.currentTime + 0.1;
    const tick = () => {
      if (!this.musicOn || !this.ctx) return;
      // schedule a little ahead of the clock so timing never jitters
      while (this.nextNoteAt < this.ctx.currentTime + 0.5) {
        this.scheduleBeat(this.nextNoteAt);
        this.nextNoteAt += 0.46;          // ~130 bpm eighth notes
        this.beat++;
      }
      this.timer = window.setTimeout(tick, 120);
    };
    tick();
  }

  stopMusic() {
    this.musicOn = false;
    if (this.timer !== null) { clearTimeout(this.timer); this.timer = null; }
  }

  private scheduleBeat(at: number) {
    const bar = Math.floor(this.beat / 8) % CHORDS.length;
    const step = this.beat % 8;
    const chord = CHORDS[bar];

    // drone: a low fifth that fades in and out across the bar
    if (step === 0) {
      this.tone(noteFreq(chord, -1), at, 3.8, 'sine', 0.10, 0.9);
      this.tone(noteFreq(chord + 4, -1), at, 3.8, 'sine', 0.05, 0.9);
    }

    // lute: a plucked arpeggio, slightly different every bar
    const arp = [0, 2, 4, 2, 5, 4, 2, 0][step];
    this.pluck(noteFreq(chord + arp, 0), at, 0.42);

    // melody: sparse, only on the strong beats, an octave up
    if (step === 0 || step === 3 || step === 6) {
      const mel = chord + [0, 4, 2, 5, 4, 2, 1, 0][(this.beat * 3) % 8];
      this.tone(noteFreq(mel, 1), at + 0.02, 0.9, 'triangle', 0.09, 0.35);
    }

    // hand drum on 1 and 5, a soft tap on the offbeats
    if (step === 0 || step === 4) this.drum(at, 0.16, 90);
    else if (step % 2 === 1) this.drum(at, 0.05, 190);
  }

  private tone(freq: number, at: number, dur: number, type: OscillatorType, vol: number, release: number) {
    if (!this.ctx || !this.musicGain) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(vol, at + 0.08);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + dur * release);
    osc.connect(gain);
    gain.connect(this.musicGain);
    osc.start(at);
    osc.stop(at + dur + 0.05);
  }

  /** Karplus-ish plucked string: a bright attack that decays fast. */
  private pluck(freq: number, at: number, dur: number) {
    if (!this.ctx || !this.musicGain) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();
    osc.type = 'sawtooth';
    osc.frequency.value = freq;
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(freq * 6, at);
    filter.frequency.exponentialRampToValueAtTime(Math.max(200, freq * 1.5), at + dur);
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(0.13, at + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.musicGain);
    osc.start(at);
    osc.stop(at + dur + 0.02);
  }

  private drum(at: number, vol: number, freq: number) {
    if (!this.ctx || !this.musicGain || !this.noise) return;
    const src = this.ctx.createBufferSource();
    const gain = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();
    src.buffer = this.noise;
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(freq * 8, at);
    filter.frequency.exponentialRampToValueAtTime(freq, at + 0.12);
    gain.gain.setValueAtTime(vol, at);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.16);
    src.connect(filter);
    filter.connect(gain);
    gain.connect(this.musicGain);
    src.start(at);
    src.stop(at + 0.2);
  }

  /* --------------------------------------------------------------- sfx */

  private burst(dur: number, vol: number, from: number, to: number, q = 1) {
    if (!this.ctx || !this.sfxGain || !this.noise) return;
    const now = this.ctx.currentTime;
    const src = this.ctx.createBufferSource();
    const gain = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();
    src.buffer = this.noise;
    src.playbackRate.value = 0.8 + Math.random() * 0.4;
    filter.type = 'bandpass';
    filter.Q.value = q;
    filter.frequency.setValueAtTime(from, now);
    filter.frequency.exponentialRampToValueAtTime(Math.max(60, to), now + dur);
    gain.gain.setValueAtTime(vol, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    src.connect(filter);
    filter.connect(gain);
    gain.connect(this.sfxGain);
    src.start(now);
    src.stop(now + dur + 0.02);
  }

  private blip(f0: number, f1: number, dur: number, type: OscillatorType, vol: number) {
    if (!this.ctx || !this.sfxGain) return;
    const now = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(f0, now);
    osc.frequency.exponentialRampToValueAtTime(Math.max(30, f1), now + dur);
    gain.gain.setValueAtTime(vol, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    osc.connect(gain);
    gain.connect(this.sfxGain);
    osc.start(now);
    osc.stop(now + dur + 0.02);
  }

  play(kind: Kind) {
    this.ensure();
    if (!this.ctx || this.sfxVolume <= 0) return;

    const wall = performance.now();
    const last = this.lastAt.get(kind) || 0;
    const minGap = kind === 'attack' || kind === 'arrow' || kind === 'death' ? 80 : 35;
    if (wall - last < minGap) return;
    this.lastAt.set(kind, wall);

    switch (kind) {
      case 'attack':            // steel on steel
        this.burst(0.16, 0.5, 3200, 900, 6);
        this.blip(520, 300, 0.09, 'triangle', 0.16);
        break;
      case 'arrow':             // a bowstring and a hiss
        this.burst(0.13, 0.35, 5200, 1200, 2);
        break;
      case 'death':             // a heavy fall
        this.burst(0.32, 0.42, 700, 90, 1);
        this.blip(180, 60, 0.28, 'sawtooth', 0.14);
        break;
      case 'build':             // hammer on timber
        this.burst(0.13, 0.4, 1400, 320, 3);
        this.blip(260, 170, 0.14, 'square', 0.12);
        break;
      case 'train':             // a horn call
        this.blip(330, 494, 0.30, 'sawtooth', 0.14);
        break;
      case 'capture':           // a soft banner flap
        this.burst(0.18, 0.22, 900, 260, 1.5);
        break;
      case 'coin':              // two bright bells
        this.blip(1180, 1180, 0.10, 'triangle', 0.16);
        this.blip(1560, 1560, 0.16, 'sine', 0.12);
        break;
      case 'meteor':            // a long roar
        this.burst(0.75, 0.6, 900, 70, 0.8);
        this.blip(150, 40, 0.7, 'sawtooth', 0.2);
        break;
      case 'ping':
        this.blip(880, 1320, 0.18, 'sine', 0.2);
        break;
      case 'notice':
        this.blip(660, 880, 0.12, 'triangle', 0.14);
        break;
      case 'error':
        this.blip(220, 150, 0.18, 'square', 0.12);
        break;
      case 'click':
      default:
        this.blip(700, 900, 0.05, 'triangle', 0.1);
        break;
    }
  }
}

export const sfx = new Audio();
